import React, { FC, useEffect, useState } from 'react'
import {
  setLastLoginMethod,
  useAuthFetch,
  useGoogleOneTap,
  useLoggedIn,
} from '@moonpig/web-core-auth'
import { logger } from '@moonpig/web-core-monitoring'
import { GOOGLE_AUTH_CLIENT_ID } from '@moonpig/web-core-brand/config'
import { useExperiment } from '@moonpig/web-core-eppo/browser'
import { redirect } from '@moonpig/web-core-routing'
import { useStoreId } from '@moonpig/web-core-stores'
import type { GoogleOneTapResponse } from './types'
import {
  trackOneTapFailedLogin,
  trackOneTapSuccessfulLogin,
  trackOneTapAccountLinkRequired,
  trackOneTapSuccessfulRegistration,
} from './tracking'
import { GoogleOneTapLinkModal } from './GoogleOneTapLinkModal'
import {
  OidcTokenLoginAccountLinkData,
  oneTapAuthenticationService,
} from './GoogleOneTapService'
import { GoogleOneTapError } from './GoogleOneTapError'

export const GoogleOneTap: FC = () => {
  const storeId = useStoreId()
  const authFetch = useAuthFetch()
  const { loggedIn, setLoggedIn } = useLoggedIn()
  const { setOneTapState } = useGoogleOneTap()
  const [showLinkModal, setShowLinkModal] = useState(true)
  const [accountLinkData, setAccountLinkData] =
    useState<OidcTokenLoginAccountLinkData>()

  const isGoogleOneTapExperimentEnabled =
    useExperiment('customer-google-one-tap', {
      fallback: 'control',
    }) === 'variant'

  const [showError, setShowError] = useState(false)

  useEffect(() => {
    if (
      !GOOGLE_AUTH_CLIENT_ID ||
      !isGoogleOneTapExperimentEnabled ||
      !window.google
    ) {
      return
    }

    function generateNonce() {
      const array = new Uint8Array(16)
      crypto.getRandomValues(array)
      return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join(
        '',
      )
    }

    const nonce = generateNonce()

    const handleCredentialResponse = async (response: GoogleOneTapResponse) => {
      try {
        setOneTapState('authenticating')

        const authService = oneTapAuthenticationService(authFetch)

        const loginResponse = await authService.oidcTokenLogin({
          token: response.credential,
          nonce,
        })

        switch (loginResponse.status) {
          case 'AUTHENTICATED': {
            setLastLoginMethod('google')
            setOneTapState('success')
            trackOneTapSuccessfulLogin()
            // Hack to show the success state before re-render on UtilityMenu
            setTimeout(() => {
              setLoggedIn(true)
            }, 2000)
            break
          }
          case 'CREATED': {
            setLastLoginMethod('google')
            setOneTapState('success')
            trackOneTapSuccessfulRegistration()
            break
          }
          case 'LINKING_REQUIRED': {
            setOneTapState('warning')
            setAccountLinkData(loginResponse.data)
            setShowLinkModal(true)
            trackOneTapAccountLinkRequired()
            break
          }
          default: {
            setShowError(true)
            setOneTapState('error')
            trackOneTapFailedLogin(loggedIn)
            break
          }
        }
      } catch (error) {
        setShowError(true)
        setOneTapState('error')
        trackOneTapFailedLogin(loggedIn)
        logger.fixToday(
          'Failed to login with Google One Tap (exception)',
          undefined,
          error,
        )
      } finally {
        setTimeout(() => {
          setOneTapState('idle')
        }, 2000)
      }
    }

    if (!loggedIn) {
      try {
        const idClient = window.google.accounts?.id
        if (idClient) {
          idClient.initialize({
            client_id: GOOGLE_AUTH_CLIENT_ID,
            callback: handleCredentialResponse,
            auto_select: false,
            cancel_on_tap_outside: false,
            nonce,
          })

          idClient.prompt()
        }
      } catch (error) {
        logger.fixToday('Failed to initialise Google One Tap', undefined, error)
      }
    }
  }, [
    loggedIn,
    setLoggedIn,
    isGoogleOneTapExperimentEnabled,
    authFetch,
    setOneTapState,
  ])

  const handleErrorLinkClick = () => {
    redirect({
      path: `/${storeId}/account/login/`,
      returnUrl: window.location.pathname + window.location.search,
    })
  }

  return (
    <>
      {accountLinkData && (
        <GoogleOneTapLinkModal
          isOpen={showLinkModal}
          onClose={() => {
            setShowLinkModal(false)
            setAccountLinkData(undefined)
          }}
          data={accountLinkData}
        />
      )}
      {showError && (
        <GoogleOneTapError
          onDismiss={() => setShowError(false)}
          onLinkClick={handleErrorLinkClick}
        />
      )}
    </>
  )
}
